import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { BUILDBROZ_LOGO_URL, usePageTitle } from '../branding';

// Only internal /ops paths are allowed; anything else falls back to /ops.
export const safeOpsNext = (raw) => {
  if (!raw || typeof raw !== 'string') return '/ops';
  const v = raw.trim();
  if (v.startsWith('//') || v.includes('://') || v.includes('\\')) return '/ops';
  if (!(v === '/ops' || v.startsWith('/ops/') || v.startsWith('/ops?'))) return '/ops';
  if (v.startsWith('/ops/login')) return '/ops';
  return v;
};

const OpsLogin = () => {
  usePageTitle('BuildBroz Ops — Sign In');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = safeOpsNext(searchParams.get('next'));

  useEffect(() => {
    // Already authenticated? Verify the existing token and go straight in.
    const token = localStorage.getItem('admin_token');
    if (!token) return;
    axios
      .get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => navigate(next, { replace: true }))
      .catch(() => localStorage.removeItem('admin_token'));
  }, [navigate, next]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);
      const response = await axios.post(`${API}/admin/login`, formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      localStorage.setItem('admin_token', response.data.access_token);
      navigate(next, { replace: true });
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many failed attempts. Please try again in 30 minutes.');
      } else {
        setError('Invalid username or password');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4" data-testid="ops-login-page">
      <Card className="bg-[#111111] border-gray-800 p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src={BUILDBROZ_LOGO_URL}
            alt="BuildBroz Logo"
            className="h-36 w-auto mx-auto mb-4 mix-blend-screen select-none pointer-events-none drop-shadow-[0_0_30px_rgba(212,175,55,0.18)]"
            data-testid="ops-login-logo"
          />
          <h1 className="text-3xl font-bold text-white mb-1">
            BuildBroz <span className="text-[#D4AF37]">Ops</span>
          </h1>
          <p className="text-gray-500 text-sm">Internal operations — sign in to continue</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-5">
          <div>
            <label className="text-white mb-2 block font-medium text-sm">Username</label>
            <Input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              required
              autoComplete="username"
              className="bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]"
              data-testid="ops-login-username"
            />
          </div>

          <div>
            <label className="text-white mb-2 block font-medium text-sm">Password</label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                autoComplete="current-password"
                className="bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37] pr-10"
                data-testid="ops-login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#D4AF37] transition-colors"
                data-testid="ops-login-toggle-password"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-900/20 border border-red-500 text-red-500 px-4 py-2 rounded text-sm" data-testid="ops-login-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold py-6"
            data-testid="ops-login-submit"
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </Button>
        </form>

        <p className="text-center mt-6 text-xs text-gray-600">
          Website admin?{' '}
          <Link to="/admin" className="text-gray-400 hover:text-[#D4AF37] transition-colors" data-testid="ops-login-admin-link">
            Go to Admin login
          </Link>
        </p>
      </Card>
    </div>
  );
};

export default OpsLogin;
